import type {
	TeaserUmbBlockElementModel,
	TeaserUmbBlockSettingsElementModel,
} from "#/_generated/umbraco/delivery-api/models";
import { Teaser } from "#/_packages/umbrella/features/teasers/components/teaser.tsx";
import type { BlockItemProps } from "#/_packages/umbrella/integrations/umbraco/types";
import { convertToCroppedFocalPointUrl } from "#/_packages/umbrella/integrations/umbraco/utils/image-utils";
import { getUmbLink } from "#/_packages/umbrella/integrations/umbraco/utils/link-utils.ts";
import type { Link } from "#/_packages/umbrella/types";
import {
	tryGetAlignment,
	tryGetAnimationEffectType,
	tryGetAnimationScrollType,
	tryGetHeadingLevel,
	tryGetIntent,
	tryGetOrientation,
	tryGetReadWidthLength,
	tryGetVariant,
} from "#/_packages/umbrella/utils";
import { getAltText } from "#/_packages/umbrella/utils/image-utils.ts";

interface ITeaserBlockProps extends BlockItemProps {
	content: TeaserUmbBlockElementModel;
	settings: TeaserUmbBlockSettingsElementModel;
}

export function TeaserUmbBlock({
	className,
	content,
	settings,
}: ITeaserBlockProps) {
	const teaserLink: Link | undefined = getUmbLink(
		content.properties?.link?.[0],
		{
			requireHref: true,
		},
	);

	return (
		<Teaser
			inlineEditing
			inlineElementKey={content.id}
			id={settings.properties?.generatedId ?? content.id}
			body={content.properties?.body?.markup ?? ""}
			className={className}
			contentAlignment={tryGetAlignment(settings.properties?.contentAlignment)}
			heading={content.properties?.heading ?? ""}
			headingLabel={content.properties?.headingLabel}
			headingLevel={tryGetHeadingLevel(content.properties?.headingLevel)}
			headingAppearance={tryGetHeadingLevel(
				content.properties?.headingAppearance,
				undefined,
			)}
			iconAlignment={tryGetAlignment(content.properties?.iconAlignment)}
			iconColor={content.properties?.iconColor}
			iconPositionTop={content.properties?.iconPositionTop ?? false}
			iconSvgId={content.properties?.icon}
			aspectRatio={content.properties?.aspectRatio}
			imageAlt={getAltText(content.properties?.image?.[0])}
			imageCaption={content.properties?.imageCaption}
			imageInBackground={content.properties?.imageInBackground ?? false}
			imageInset={content.properties?.imageInset ?? false}
			imagePositionEnd={content.properties?.imagePositionEnd ?? false}
			imageSrc={
				content.properties?.image?.[0]
					? convertToCroppedFocalPointUrl(content.properties?.image?.[0], {
							aspectRatio: content.properties?.aspectRatio ?? undefined,
						})
					: undefined
			}
			intentColor={tryGetIntent(content.properties?.intentColor)}
			link={teaserLink}
			orientation={tryGetOrientation(content.properties?.orientation)}
			typeVariant={tryGetVariant(content.properties?.typeVariant)}
			animationHoverType={settings.properties?.animationHoverType ?? []}
			animationScrollType={tryGetAnimationScrollType(
				settings.properties?.animationScrollType,
			)}
			animationEffectType={tryGetAnimationEffectType(
				settings.properties?.animationEffectType,
			)}
			readWidthToggle={settings.properties?.readWidthToggle ?? false}
			readWidthLength={tryGetReadWidthLength(
				settings.properties?.readWidthLengthPicker,
			)}
		/>
	);
}
